import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { web } from "@kikoff/proto/src/protos";
import { webRPC } from "@kikoff/proto/src/rpc";
import { handleFailedStatus, handleProtoStatus } from "@kikoff/utils/src/proto";
import Table from "@kikoff/utils/src/table";

import { RootState } from "@store";

import { createLoadableSelector, thunk } from "../utils";

const initialState = {
  interestRatesByLoanTerm: null as InterestRatesByLoanTerm,
  estimatedAutoLoanByToken: {} as EstimatedAutoLoan.ByToken,
  estimatedAutoLoanTokens: [] as string[],
};

export type AutoLoansState = typeof initialState;

const autoLoansSlice = createSlice({
  name: "autoLoans",
  initialState,
  reducers: {
    setEstimatedAutoLoans(
      state,
      { payload: estimatedAutoLoans }: PayloadAction<EstimatedAutoLoan[]>
    ) {
      state.estimatedAutoLoanTokens = estimatedAutoLoans.map(
        (loan) => loan.token
      );

      state.estimatedAutoLoanByToken = EstimatedAutoLoan.ByToken.fromList(
        estimatedAutoLoans
      );
    },
    setInterestRatesByLoanTerm(
      state,
      { payload }: PayloadAction<InterestRatesByLoanTerm>
    ) {
      state.interestRatesByLoanTerm = payload;
    },
  },
});

const { actions } = autoLoansSlice;
export const {} = actions;
export default autoLoansSlice.reducer;

export const selectInterestRatesByLoanTerm = createLoadableSelector(
  () => (state: RootState) => state.autoLoans?.interestRatesByLoanTerm,
  {
    loadAction: () => fetchUserInterestRatesByLoanTerm(),
  }
);

export const selectAutoLoanTokens = createLoadableSelector(
  () => (state: RootState) => state.autoLoans.estimatedAutoLoanTokens,
  {
    loadAction: () => fetchEstimatedUserAutoLoans(),
    selectLoaded: () => (state: RootState) =>
      !!state.autoLoans.estimatedAutoLoanTokens.length,
  }
);

export const selectAutoLoanEstimates = createLoadableSelector(
  () => (state: RootState) =>
    state.autoLoans.estimatedAutoLoanTokens?.map(
      (token) => state.autoLoans.estimatedAutoLoanByToken[token]
    ),
  {
    loadAction: () => fetchEstimatedUserAutoLoans(),
    selectLoaded: () => (state: RootState) =>
      !!state.autoLoans.estimatedAutoLoanTokens.length,
  }
);

export const selectAutoLoanEstimate = createLoadableSelector(
  (token: EstimatedAutoLoan.Token) => (state: RootState) =>
    state.autoLoans.estimatedAutoLoanByToken[token],
  {
    loadAction: () => fetchEstimatedUserAutoLoans(),
    selectLoaded: () => (state: RootState) =>
      !!state.autoLoans.estimatedAutoLoanTokens.length,
  }
);

export const selectMostRecentAutoLoanEstimate = createLoadableSelector(
  () => (state: RootState) =>
    state.autoLoans.estimatedAutoLoanByToken[
      state.autoLoans.estimatedAutoLoanTokens[0]
    ],
  {
    loadAction: () => fetchEstimatedUserAutoLoans(),
    selectLoaded: () => (state: RootState) =>
      !!state.autoLoans.estimatedAutoLoanTokens.length,
  }
);

export const fetchUserInterestRatesByLoanTerm = () =>
  thunk((dispatch) =>
    webRPC.AutoLoans.userInterestRatesByLoanTerm({}).then(
      handleProtoStatus({
        SUCCESS({ interestRatesByLoanTerm }) {
          dispatch(actions.setInterestRatesByLoanTerm(interestRatesByLoanTerm));
        },
        _DEFAULT: handleFailedStatus("Failed to fetch user interest rates."),
      })
    )
  );

export const estimateUserAutoLoan = (
  autoLoanForm: web.public_.IEstimateUserAutoLoanRequest
) =>
  thunk((dispatch) =>
    webRPC.AutoLoans.estimateUserAutoLoan(autoLoanForm).then(
      handleProtoStatus({
        SUCCESS({ estimatedAutoLoan }) {
          dispatch(actions.setEstimatedAutoLoans([estimatedAutoLoan]));
          return estimatedAutoLoan;
        },
        _DEFAULT: handleFailedStatus("Failed to submit loan."),
      })
    )
  );
export const updateUserAutoLoanEstimate = (
  partialloanEstimateForm: web.public_.IUpdateUserAutoLoanEstimateRequest
) =>
  thunk((dispatch) =>
    webRPC.AutoLoans.updateUserAutoLoanEstimate(partialloanEstimateForm).then(
      handleProtoStatus({
        SUCCESS({ estimatedAutoLoan }) {
          dispatch(actions.setEstimatedAutoLoans([estimatedAutoLoan]));
        },
        _DEFAULT: handleFailedStatus("Failed to fetch loan preview offers."),
      })
    )
  );
export const fetchEstimatedUserAutoLoans = () =>
  thunk((dispatch) =>
    webRPC.AutoLoans.listEstimatedUserAutoLoans({}).then(
      handleProtoStatus({
        SUCCESS({ estimatedAutoLoans }) {
          dispatch(actions.setEstimatedAutoLoans(estimatedAutoLoans));
          return estimatedAutoLoans;
        },
        PENDING: () => false,
        _DEFAULT: handleFailedStatus("Failed to fetch loan offers."),
      })
    )
  );

export const submitAutoLoanQuestion = ({
  token,
  questionText,
}: web.public_.ISubmitAutoLoanQuestionRequest) =>
  thunk(() =>
    webRPC.AutoLoans.submitAutoLoanQuestion({ token, questionText }).then(
      handleProtoStatus({
        SUCCESS({}) {},
        _DEFAULT: handleFailedStatus("Failed to submit auto loan question."),
      })
    )
  );

export const formatNumberWithCommas = (input: string) => {
  // remove all non-numeric characters and format number with commas
  const numeric = input.replace(/\D/g, "");
  return numeric.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

export type EstimatedAutoLoan = web.public_.IEstimatedAutoLoan;
export type InterestRatesByLoanTerm = Record<number, string>;

export namespace EstimatedAutoLoan {
  export type Token = string & {};
  export type ByToken = Record<EstimatedAutoLoan.Token, EstimatedAutoLoan>;

  export namespace ByToken {
    export const fromList = (estimates: EstimatedAutoLoan[]) =>
      Table.createIndex(estimates, ["token"]);
  }

  export const BudgetType = web.public_.AutoLoanBudget.BudgetType;
}
